/* eslint-disable max-lines */
import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TwoColumnLayout } from "../templates";
import { GoogleRecaptcha, FormField } from "../components";
import { notifySuccess, notifyError } from "../lib/notifications";
import { language, settings, animations } from "../settings";
import { forgotPassword } from "../api/forgot-password";
import { checkEmailValid } from "../helpers";

const COUNTDOWN_DURATION = 60 * 1000;
const STORAGE_KEY = "passwordResetCountdown";

export const ForgottenPassword = () => {
  const url = new URL(window.location.href);
  const emailParam = url.searchParams.get("email") || "";

  const recaptchaRef = React.createRef();
  const countdownIntervalRef = useRef(null);

  const [email, setEmail] = useState(emailParam);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const { animate } = settings;
  const { form } = animations;

  const startCountdown = (endTime) => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      
      setTimeRemaining(remaining);
      
      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        localStorage.removeItem(STORAGE_KEY);
      }
    }, 1000);
  };

  useEffect(() => {
    const savedEndTime = localStorage.getItem(STORAGE_KEY);
    if (savedEndTime) {
      const endTime = parseInt(savedEndTime, 10);
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      
      if (remaining > 0) {
        setTimeRemaining(remaining);
        startCountdown(endTime);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const startCountdownTimer = () => {
    const endTime = Date.now() + COUNTDOWN_DURATION;
    localStorage.setItem(STORAGE_KEY, endTime.toString());
    setTimeRemaining(COUNTDOWN_DURATION);
    startCountdown(endTime);
  };

  const isButtonDisabled = isLoading || timeRemaining > 0;

  const getButtonText = () => {
    if (isLoading) return "Sending...";
    if (timeRemaining > 0) return `Try again in ${formatTime(timeRemaining)}`;
    return "Reset password";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!checkEmailValid(email)) {
      return notifyError("Please enter a valid email");
    }

    if (!recaptchaRef.current) {
      return notifyError("Recaptcha not initialized. Please refresh the page.");
    }

    setIsLoading(true);

    try {
      const recaptchaToken = await recaptchaRef.current.executeAsync();

      if (!recaptchaToken) {
        setIsLoading(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        startCountdownTimer();
        return notifyError("Failed to generate captcha token. Please try again after the countdown.");
      }

      const body = {
        username: email,
        recaptchaToken,
      };

      await forgotPassword(body);
      
      setHasSubmitted(true);
      setEmail("");
      notifySuccess("Email with reset password link has been sent");
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      startCountdownTimer();
    } catch (error) {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

      startCountdownTimer();
      
      if (!error.response) {
        notifyError("Network error");
      } else if (error.response.status === 400) {
        notifyError("Wrong or expired captcha. Please try again after the countdown.");
      } else if (error.response.status === 429) {
        notifyError("Too many requests, please try again after the countdown.");
      } else {
        notifyError("Something went wrong. Please try again after the countdown.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const leftContent = (
    <div className="absolute" style={{ inset: -2 }}>
      <picture>
        <source
          media="(min-width: 1024px)"
          srcSet="/design/images/login-page/forgotten-password-desktop.webp"
        />
        <img
          src="/design/images/login-page/forgotten-password-tablet.webp"
          alt="Forgotten password illustration"
          className="w-full h-full object-cover"
        />
      </picture>
      <div className="absolute z-10" style={{ top: '2.5rem', left: '2.5rem' }}>
        <img
          src="/design/images/login-page/breathe_logo_white.svg"
          alt="Breathe logo icon"
          className="h-8 w-9"
        />
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{language("forgottenPassword")}</title>
      </Helmet>

      <TwoColumnLayout leftContent={leftContent}>
        {/* Invisible recaptcha - must be in DOM for proper initialization */}
        <GoogleRecaptcha forwardedRef={recaptchaRef} />
        <div className="flex flex-col h-full">
          {/* Content */}
          <div className="flex-1 flex flex-col items-center justify-center">
            <motion.div
              variants={form}
              initial={animate ? "hidden" : ""}
              animate={animate ? "show" : ""}
              className="w-full max-w-md"
            >
              {/* Header */}
              <div className="mb-5">
                <h1 className="heading-responsive font-medium text-black tracking-tight mb-2">
                  Reset your password
                </h1>
              </div>

              {/* Success Message */}
              {hasSubmitted && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-semibold text-blue-900 mb-1">
                    {language("forgotPasswordTitle")}
                  </p>
                  <p className="text-xs text-blue-800">
                    {language("forgotPasswordText")}
                  </p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="flex flex-col gap-3 items-start w-full">
                <FormField
                  id="reset-email"
                  type="email"
                  label="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="me@example.com"
                  disabled={isLoading}
                  required
                />

                <button
                  type="submit"
                  disabled={isButtonDisabled}
                  className="w-full login-button rounded-md login-bg-primary text-white text-sm font-normal flex items-center justify-center font-inter disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {getButtonText()}
                </button>
              </form>

              {/* Bottom Link */}
              <div className="mt-6 text-center">
                <p className="text-sm login-text-foreground text-center font-rubik">
                  Remembered your password?{" "}
                  <Link
                    to="/login"
                    className="underline font-rubik"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </TwoColumnLayout>
    </>
  );
};
